// War Room FC Leaderboard (Task 11): every UPS-matrix origin zip ranked by portfolio-wide 2-day
// ground demand coverage, alongside our current occupancy there — surfaces high-value buildings
// we're not stocking at all (units_held = 0 AND portfolio_dw_reach_2d >= 50) as expansion
// targets. Fetches GET /api/war-room/fc-leaderboard once; sortable table follows the same
// click-header asc/desc pattern as PlanningTable.
import React, { useEffect, useMemo, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import WarRoomTabs from './WarRoomTabs';

const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

// ---- sorting: numeric-aware, nulls always sort last regardless of direction (mirrors PlanningTable) ----
function compareValues(aVal, bVal, dir, numeric) {
    const aNull = aVal === null || aVal === undefined || aVal === '';
    const bNull = bVal === null || bVal === undefined || bVal === '';
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    let cmp;
    if (numeric) {
        cmp = Number(aVal) - Number(bVal);
    } else {
        cmp = String(aVal).localeCompare(String(bVal));
    }
    return dir === 'asc' ? cmp : -cmp;
}

const COLUMNS = [
    { key: 'fcCodes', label: 'FC codes', numeric: false, getValue: (r) => r.fcCodes },
    { key: 'city', label: 'City/ST', numeric: false, getValue: (r) => `${r.fcCity || ''}, ${r.fcState || ''}` },
    { key: 'originZip', label: 'Zip', numeric: false, getValue: (r) => r.originZip },
    { key: 'reach1d', label: 'Reach 1d', numeric: true, getValue: (r) => r.reach1d },
    { key: 'reach2d', label: 'Reach 2d', numeric: true, getValue: (r) => r.reach2d },
    { key: 'portfolioDwReach2d', label: 'Portfolio demand ≤2d %', numeric: true, getValue: (r) => r.portfolioDwReach2d },
    { key: 'asinsStocked', label: 'Our ASINs', numeric: true, getValue: (r) => r.asinsStocked },
    { key: 'unitsHeld', label: 'Our units', numeric: true, getValue: (r) => r.unitsHeld },
];

function isExpansionTarget(row) {
    return (row.unitsHeld || 0) === 0 && (row.portfolioDwReach2d || 0) >= 50;
}

// ---- Task 28: MP weakness map (GET /api/war-room/mp-weakness-map). Verdict pills: TARGET =
// attackable (MP >=0.5d slower than achievable from an existing building + demand >=2%), DESERT =
// both sellers >=6.5d, STRONGHOLD = MP faster than us by >=0.3d.
const VERDICT_STYLES = {
    TARGET: 'bg-green-100 text-green-800 border border-green-300',
    DESERT: 'bg-gray-100 text-gray-600 border border-gray-300',
    STRONGHOLD: 'bg-red-100 text-red-800 border border-red-300',
};

const fmtDays = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}d`);

function MpWeaknessMap({ rows, error }) {
    return (
        <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">MP weakness map</h2>
                <p className="text-xs text-gray-500">
                    Marketplace Pros vs our portfolio-wide fastest delivery promise per metro (trailing 7 days),
                    sorted by demand. Green = the faster promise.
                </p>
            </div>
            {error && (
                <div className="p-4 text-sm text-red-800 bg-red-50">{error}</div>
            )}
            {!error && (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                <th className="px-3 py-2 whitespace-nowrap">Metro</th>
                                <th className="px-3 py-2 text-right whitespace-nowrap">Demand %</th>
                                <th className="px-3 py-2 text-center whitespace-nowrap">Our days</th>
                                <th className="px-3 py-2 text-center whitespace-nowrap">MP days</th>
                                <th className="px-3 py-2 whitespace-nowrap">Verdict</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-xs">
                            {rows.map((r) => {
                                const weFaster = r.ourDays != null && r.mpDays != null && r.ourDays < r.mpDays;
                                const mpFaster = r.ourDays != null && r.mpDays != null && r.mpDays < r.ourDays;
                                return (
                                    <tr key={r.metroZip}>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className="font-medium text-gray-800">{r.city ? `${r.city}, ${r.state || ''}` : '—'}</span>
                                            <span className="ml-1.5 font-mono text-gray-400">{r.metroZip}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                                            {r.demandPct != null ? `${Number(r.demandPct).toFixed(1)}%` : '—'}
                                        </td>
                                        <td className={`px-3 py-2 text-center font-mono tabular-nums ${weFaster ? 'text-green-700 font-semibold' : ''}`}>
                                            {fmtDays(r.ourDays)}
                                        </td>
                                        <td className={`px-3 py-2 text-center font-mono tabular-nums ${mpFaster ? 'text-green-700 font-semibold' : ''}`}>
                                            {fmtDays(r.mpDays)}
                                        </td>
                                        <td className="px-3 py-2">
                                            {r.verdict && r.verdict !== '—' ? (
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${VERDICT_STYLES[r.verdict] || VERDICT_STYLES.DESERT}`}>
                                                    {r.verdict}
                                                </span>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {rows.length === 0 && (
                        <div className="p-6 text-center text-sm text-gray-500">No MP promise data yet.</div>
                    )}
                </div>
            )}
        </div>
    );
}

function FcLeaderboard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sort, setSort] = useState({ field: 'portfolioDwReach2d', dir: 'desc' });
    // Task 28: MP weakness map -- fetched independently so an MP-slice hiccup never blanks the
    // FC leaderboard itself (and vice versa).
    const [mpRows, setMpRows] = useState([]);
    const [mpError, setMpError] = useState(null);

    useEffect(() => {
        ApiClient.get('/api/war-room/fc-leaderboard')
            .then((response) => {
                setRows(response.data || []);
                setError(null);
            })
            .catch((err) => {
                console.error('Error fetching FC leaderboard:', err);
                setError('Failed to load the FC leaderboard. Please try again.');
            })
            .finally(() => setLoading(false));
        ApiClient.get('/api/war-room/mp-weakness-map')
            .then((response) => {
                setMpRows(response.data || []);
                setMpError(null);
            })
            .catch((err) => {
                console.error('Error fetching MP weakness map:', err);
                setMpError('Failed to load the MP weakness map.');
            });
    }, []);

    const handleSort = (field) => {
        setSort((prev) => {
            if (prev.field === field) {
                return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            }
            return { field, dir: 'desc' };
        });
    };

    const sorted = useMemo(() => {
        const col = COLUMNS.find((c) => c.key === sort.field) || COLUMNS.find((c) => c.key === 'portfolioDwReach2d');
        return [...rows].sort((a, b) => compareValues(col.getValue(a), col.getValue(b), sort.dir, col.numeric));
    }, [rows, sort]);

    const sortIndicator = (key) => {
        if (sort.field !== key) return null;
        return sort.dir === 'asc' ? ' ▲' : ' ▼';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <svg className="animate-spin h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">War Room FC Leaderboard</h1>
                <p className="text-sm text-gray-500">
                    Every UPS-matrix origin building ranked by portfolio-wide 2-day ground demand coverage,
                    regardless of what we currently stock there.
                </p>
            </div>

            <WarRoomTabs active="FC Leaderboard" />

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                    {error}
                </div>
            )}

            {/* Task 28: per-metro MP weakness map, above the FC list. */}
            <MpWeaknessMap rows={mpRows} error={mpError} />

            <div className="bg-white rounded-lg shadow border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                {COLUMNS.map((col) => (
                                    <th
                                        key={col.key}
                                        onClick={() => handleSort(col.key)}
                                        className="px-3 py-2 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap"
                                    >
                                        {col.label}
                                        {sortIndicator(col.key)}
                                    </th>
                                ))}
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-xs">
                            {sorted.map((row) => {
                                const expansionTarget = isExpansionTarget(row);
                                return (
                                    <tr key={row.originZip} className={expansionTarget ? 'bg-green-50' : ''}>
                                        <td className="px-3 py-2 font-mono font-medium text-gray-700 whitespace-nowrap">{row.fcCodes || '—'}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">{row.fcCity ? `${row.fcCity}, ${row.fcState || ''}` : '—'}</td>
                                        <td className="px-3 py-2 font-mono">{row.originZip}</td>
                                        <td className="px-3 py-2 text-center">{fmtNum(row.reach1d)}</td>
                                        <td className="px-3 py-2 text-center">{fmtNum(row.reach2d)}</td>
                                        <td className="px-3 py-2 text-center font-semibold">
                                            {row.portfolioDwReach2d != null ? `${Number(row.portfolioDwReach2d).toFixed(1)}%` : '—'}
                                        </td>
                                        <td className="px-3 py-2 text-center">{fmtNum(row.asinsStocked)}</td>
                                        <td className="px-3 py-2 text-center">{fmtNum(row.unitsHeld)}</td>
                                        <td className="px-3 py-2">
                                            {expansionTarget && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-300">
                                                    expansion target
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {sorted.length === 0 && (
                    <div className="p-8 text-center text-sm text-gray-500">No FC leaderboard data yet.</div>
                )}
            </div>
        </div>
    );
}

export default FcLeaderboard;
